import styled from "@emotion/styled";
import { ListItemButton } from "@mui/material";
import { Button, Typography } from "@avenue-8/sidekick-ui";
import { useUIStore } from "@store/ui.store";
import { getAppIcon } from "@features/ConnectedApps/app-connection-helpers";
import { useEnableGoogleContactsExtraction } from "@features/ConnectedApps/useEnableGoogleContactsExtraction";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type CardSize = "small" | "medium";

type Props = {
  isConnected: boolean;
  size?: CardSize | "banner";
};

export const ContactsExtractionConsent = ({
  isConnected,
  size = "medium",
}: Props) => {
  const openSettingsModal = useUIStore((state) => state.openSettingsModal);
  const { enableGoogleContactsExtraction, isEnabling } =
    useEnableGoogleContactsExtraction();

  const handleClick = () => {
    if (isConnected) {
      enableGoogleContactsExtraction();
    } else {
      openSettingsModal("integrations", { highlightAppId: "google" });
    }
  };

  if (size === "banner") {
    return (
      <ConsentBanner
        isConnected={isConnected}
        onClick={handleClick}
        disabled={isEnabling}
      />
    );
  }

  return (
    <ConsentCard
      size={size}
      isConnected={isConnected}
      isEnabling={isEnabling}
      onConnectClick={handleClick}
    />
  );
};

type ConsentBannerProps = {
  isConnected: boolean;
  onClick: () => void;
  disabled?: boolean;
};

const ConsentBanner = ({
  isConnected,
  onClick,
  disabled,
}: ConsentBannerProps) => {
  const bodyText = isConnected
    ? "Your Google account is connected. Grant permission to automatically import more contacts and leads."
    : "Connect Google to automatically import more contacts and leads from your inbox.";

  return (
    <BannerContainer onClick={onClick} disabled={disabled}>
      <BannerIcon>{getAppIcon("google")}</BannerIcon>
      <BannerContent>
        <BannerTitle variant="subtitle2" color="text.primary" mb={0.5}>
          Extract contacts and leads from your email inbox
        </BannerTitle>
        <Typography variant="body2" color="text.primary">
          {bodyText}
        </Typography>
      </BannerContent>
      <ChevronWrap>
        <ChevronRightIcon aria-hidden />
      </ChevronWrap>
    </BannerContainer>
  );
};

type ConsentCardProps = {
  size: CardSize;
  isConnected: boolean;
  isEnabling?: boolean;
  onConnectClick: () => void;
};

const ConsentCard = ({
  size,
  isConnected,
  isEnabling,
  onConnectClick,
}: ConsentCardProps) => {
  const bodyText = isConnected
    ? "Your Google account is connected. Grant Sidekick permission to read your inbox and automatically identify and import your contacts and leads."
    : "Connect your Google account so Sidekick can read your inbox and automatically identify and import your contacts and leads.";
  const buttonLabel = isConnected ? "Grant permission" : "Connect Google";
  const buttonIcon = isConnected ? undefined : getAppIcon("google");

  return (
    <CardContainer $size={size}>
      <CardIcon $size={size}>
        <MailOutlineIcon fontSize={size === "medium" ? "medium" : "inherit"} />
      </CardIcon>
      <Typography
        variant={size === "medium" ? "subtitle1" : "h6"}
        mb={size === "small" ? 1 : undefined}
        sx={size === "medium" ? { lineHeight: 1.2 } : undefined}
      >
        Extract contacts and leads from your email inbox
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2} px={2}>
        {bodyText}
      </Typography>
      <Button
        variant="outlined"
        onClick={onConnectClick}
        startIcon={buttonIcon}
        size="small"
        disabled={isEnabling}
      >
        {buttonLabel}
      </Button>
    </CardContainer>
  );
};

const BannerContainer = styled(ListItemButton)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  text-align: left;
  border: 1px solid ${(p) => p.theme.colors.border2};
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background-color: ${(p) => p.theme.colors.background5};
    border-color: ${(p) => p.theme.text.colors.brand};
  }
`;

const BannerIcon = styled.div`
  display: flex;
  flex-shrink: 0;
  color: ${(p) => p.theme.text.colors.primary};
  font-size: ${(p) => p.theme.title.fontSizes.h3};

  img {
    width: 1.5rem;
    height: 1.5rem;
    object-fit: contain;
  }
`;

const BannerContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const BannerTitle = styled(Typography)`
  line-height: 1.2;
`;

const ChevronWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${(p) => p.theme.text.colors.primary};
`;

const CardContainer = styled.div<{ $size: CardSize }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
  max-width: 680px;
  margin: 0 auto;

  ${(p) =>
    p.$size === "small" &&
    `
    padding: 0 1.5rem;
    margin: 3rem auto;
  `}
`;

const CardIcon = styled.div<{ $size: CardSize }>`
  display: flex;
  align-items: center;
  justify-content: center;

  ${(p) =>
    p.$size === "medium"
      ? `
    width: 3rem;
    height: 3rem;
    margin-bottom: 0.5rem;
    border-radius: 50%;
    background-color: ${p.theme.colors.background5};
    color: ${p.theme.text.colors.brand};
  `
      : `
    font-size: ${p.theme.title.fontSizes.h3};
    color: ${p.theme.text.colors.primary};
  `}
`;
